import { makeAutoObservable, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { AxiosError } from 'axios'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { isLink, logger } from 'shared/lib'
import { type IDropdownItem, showToast, Typography } from 'shared/ui'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import {
  type IParamsCreateCustomDomain,
  type IResponseCustomDomain,
  ShortLinkApi,
} from 'entities/ShortLink'
import { UrlShortener } from 'entities/ShortLink/model/UrlShortener'
import { BillingApi, PriceInfoStore } from 'entities/Billing'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { AddCustomDomainModalContent } from 'pages/settings/pages/urlShortener/ui/AddCustomDomainModalContent/AddCustomDomainModalContent'
import {
  enumDomainOption,
  enumSubdomainOption,
  type IDomainsOptions,
  type ISubdomainsOptions,
} from 'pages/settings/pages/urlShortener/store/types'
import { editCustomDomain } from 'pages/settings/pages/urlShortener/ui/actions/actionItems/editCustomDomain'
import { ActivateCustomDomainModalContent } from 'pages/settings/pages/urlShortener/ui/ActivateCustomDomainModalContent/ActivateCustomDomainModalContent'
import { AddCustomDomainModalAction } from 'pages/settings/pages/urlShortener/ui/AddCustomDomainModalAction/AddCustomDomainModalAction'
import { ActivateCustomDomainModalAction } from 'pages/settings/pages/urlShortener/ui/ActivateCustomDomainModalAction/ActivateCustomDomainModalAction'

export class UrlShortenerModalStore {
  private _idModal = nanoid()
  private _loading = false
  private _errorsMap = new Map<string, string>()
  private _domainName = ''
  private _domainOption: IDomainsOptions | null = null
  private _fallbackUrl = ''
  private _subdomainOption: ISubdomainsOptions = enumSubdomainOption.go
  private _customSubdomainName = ''
  private _urlItem: UrlShortener | IResponseCustomDomain | null = null
  private _isPaymentError = false
  private readonly _priceInfoStore: PriceInfoStore

  constructor(
    private _entities: IEntitiesStore,
    private _getCustomDomain: () => Promise<IResponseCustomDomain | never[]>
  ) {
    makeAutoObservable(this)

    this._priceInfoStore = new PriceInfoStore((count, cancelToken) =>
      BillingApi.getBillingPreviewCustomDomain(
        {
          plan: this._entities.subscriptionStore.plan?.custom_domain_plan_id || '',
        },
        {
          cancelToken: cancelToken,
        }
      )
    )
  }

  openAddCustomDomainModal = () => {
    modalStore.addModal({
      id: this._idModal,
      title: 'Add custom domain',
      width: 480,
      onClose: this.closeModal,
      showCloseButton: false,
      ModalContent: () => <AddCustomDomainModalContent store={this} />,
      ModalActions: () => <AddCustomDomainModalAction store={this} />,
    })
  }

  closeModal = () => {
    modalStore.removeModal(this._idModal)
  }

  handleContinue = () => {
    if (
      this._domainOption === enumDomainOption.links &&
      !!this._fallbackUrl &&
      !this.isFallbackUrlValid
    ) {
      this.setError('fallback_url_failed', 'Please enter a valid URL')
      return
    }

    if (
      this._domainOption === enumDomainOption.website &&
      this._subdomainOption === enumSubdomainOption.custom &&
      !this._customSubdomainName
    ) {
      this.setError('custom_subdomain_name', 'Please enter a URL')
      return
    }

    void this.createCustomDomain()
  }

  createCustomDomain = async () => {
    try {
      runInAction(() => {
        this._loading = true
      })

      const { data } = await ShortLinkApi.createCustomDomain(this.createParams)

      if (data.status === 'fallback_url_failed') {
        this.setError(data.status, data.failed_reason)
      } else if (data.status === 'pending') {
        await this.handleSuccessCreateCustomDomain()
      }
    } catch (e) {
      logger.error(e)

      showToast({
        type: 'error',
        title: 'Something went wrong. Please try again',
      })
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  handleSuccessCreateCustomDomain = async () => {
    const response = await this._getCustomDomain()
    const urlItem = response && !Array.isArray(response) ? new UrlShortener(response) : undefined

    this.openVerifyCustomDomainModal(urlItem)
  }

  openVerifyCustomDomainModal = (urlItem?: UrlShortener | IResponseCustomDomain) => {
    const action = editCustomDomain(this, urlItem)

    if (action && action.onAction) action.onAction()
  }

  handleBack = () => {
    this.removeError('fallback_url_failed')
    this.removeError('custom_subdomain_name')
    this._domainOption = this._urlItem?.fallback_url ? enumDomainOption.links : null
    this._subdomainOption = enumSubdomainOption.go
    this.openAddCustomDomainModal()
  }

  handleVerify = async () => {
    try {
      if (typeof this._urlItem?.id === 'undefined') return

      runInAction(() => {
        this._loading = true
      })

      const isNeedVerifyCustomDomain =
        this._urlItem?.status !== 'certificate_in_progress' &&
        this._urlItem?.status !== 'certificate_processed' &&
        this._urlItem?.status !== 'waiting_for_payment'

      if (isNeedVerifyCustomDomain) {
        const { data } = await ShortLinkApi.verifyCustomDomain(this._urlItem.id)

        runInAction(() => {
          this._urlItem = data
        })

        if (data.status === 'certificate_in_progress') this.openVerifyProgressModal()
      } else {
        this.closeModal()
      }

      await this._getCustomDomain()
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  openVerifyProgressModal = () => {
    modalStore.addModal({
      id: this._idModal,
      title: 'Verification in progress',
      desc: (
        <>
          <Typography
            ariaLabel={'verificationDescription'}
            variant={'body-rg-regular'}
            color={'var(--content-primary-tertiary)'}
            tag={'div'}
            marginBottom={'20px'}
          >
            We’re checking your DNS records. This can take up to 48 hours, depending on your domain
            provider.
          </Typography>
          <Typography
            ariaLabel={'verificationDescription'}
            variant={'body-rg-regular'}
            color={'var(--content-primary-tertiary)'}
            tag={'div'}
          >
            Once verified, return here
          </Typography>
          <Typography
            ariaLabel={'verificationDescription'}
            variant={'body-rg-regular'}
            color={'var(--content-primary-tertiary)'}
            tag={'div'}
          >
            to activate URL Shortener.
          </Typography>
        </>
      ),
      type: ModalTypeList.INFO,
      showCloseButton: false,
      onClose: this.closeModal,
      primaryAction: {
        text: 'Got it',
        onAction: this.closeModal,
      },
    })
  }

  openActivateCustomDomainModal = () => {
    this._isPaymentError = false

    modalStore.addModal({
      id: this._idModal,
      title: 'Activate custom domain',
      width: 480,
      onClose: this.closeModal,
      showCloseButton: false,
      ModalContent: () => <ActivateCustomDomainModalContent store={this} />,
      ModalActions: () => <ActivateCustomDomainModalAction store={this} />,
    })
  }

  handleActivate = async () => {
    try {
      runInAction(() => {
        this._loading = true
        this._isPaymentError = false
      })

      const { data } = await BillingApi.upgradeBillingCustomDomain()

      if (data.status === 'payed') {
        showToast({
          type: 'success',
          title: 'Branded URL Shortener activated',
          desc: 'Your custom domain will be used for URL shortening',
        })
      } else {
        runInAction(() => {
          this._isPaymentError = true
        })

        showToast({
          type: 'error',
          title: 'Payment failed. The domain isn’t active.',
        })
      }

      this.closeModal()
    } catch (e) {
      logger.error(e)

      if (e instanceof AxiosError) {
        showToast({
          type: 'error',
          title: e.response?.data.failed_reason || 'Something went wrong. Please try again',
        })

        if (e.response?.data.status === 'payment_failed') {
          runInAction(() => {
            this._isPaymentError = true
          })
        } else {
          this.closeModal()
        }
      }
    } finally {
      void this._getCustomDomain()

      runInAction(() => {
        this._loading = false
      })
    }
  }

  setDomainName = (value: string) => {
    this._domainName = value
  }

  setError = (key: string, error: string) => {
    this._errorsMap.set(key, error)
  }

  removeError = (key: string) => {
    this._errorsMap.delete(key)
  }

  setDomainOption = (value: IDomainsOptions) => {
    switch (value) {
      case enumDomainOption.links:
        this.setCustomSubdomainName('')
        this.setSubdomainOption(enumSubdomainOption.go)
        break
      case enumDomainOption.website:
        this.setFallbackUrl('')
        break
    }

    this._domainOption = value
  }

  setFallbackUrl = (value: string) => {
    this.removeError('fallback_url_failed')
    this._fallbackUrl = value
  }

  setSubdomainOption = (value: enumSubdomainOption) => {
    if (value !== enumSubdomainOption.custom) this._customSubdomainName = ''
    this._subdomainOption = value
  }

  setCustomSubdomainName = (value: string) => {
    this.removeError('custom_subdomain_name')
    this._customSubdomainName = value
  }

  setUrlItem = (value: UrlShortener | IResponseCustomDomain | null) => {
    this._urlItem = value
  }

  reset = () => {
    this._loading = false
    this._domainName = ''
    this._domainOption = null
    this._subdomainOption = enumSubdomainOption.go
    this._fallbackUrl = ''
    this._customSubdomainName = ''
    this._urlItem = null
    this._errorsMap.clear()
    this._isPaymentError = false
  }

  get loading() {
    return this._loading
  }

  get errorsMap() {
    return this._errorsMap
  }

  get isContinueBtnDisabled() {
    return !this.isDomainNameValid || !this._domainOption
  }

  get domainName() {
    return this._domainName
  }

  get isDomainNameValid() {
    return isLink(`https://${this._domainName}`)
  }

  get isFallbackUrlValid() {
    return isLink(this._fallbackUrl)
  }

  get domainOption() {
    return this._domainOption
  }

  get fallbackUrl() {
    return this._fallbackUrl
  }

  get customSubdomainName() {
    return this._customSubdomainName
  }

  get subdomainOption() {
    return this._subdomainOption
  }

  get subdomainOptions() {
    const options: IDropdownItem[] = []

    Object.values(enumSubdomainOption).forEach((option) => {
      options.push({
        id: option,
        label:
          option === enumSubdomainOption.custom
            ? 'Custom subdomain'
            : `${option}.${this._domainName}`,
      })
    })

    return options
  }

  get createParams() {
    const params: IParamsCreateCustomDomain = {
      domain: this._domainName,
    }

    switch (this._domainOption) {
      case enumDomainOption.links:
        if (this._fallbackUrl) params.fallback_url = this._fallbackUrl
        break
      case enumDomainOption.website:
        if (this._subdomainOption === enumSubdomainOption.custom) {
          params.domain = `${this._customSubdomainName}.${this._domainName}`
        } else {
          params.domain = `${this._subdomainOption}.${this._domainName}`
        }
        break
    }

    return params
  }

  get clipboardRequiredDataText() {
    return window.Config?.shortener_ip_a || ''
  }

  get urlItem() {
    return this._urlItem
  }

  get idModal() {
    return this._idModal
  }

  get priceInfoStore() {
    return this._priceInfoStore
  }

  get isPaymentError() {
    return this._isPaymentError
  }

  get isStatusPendingPayment() {
    return this.urlItem?.status === 'waiting_for_payment'
  }

  get isStatusUnsubscribed() {
    return this.urlItem?.status === 'unsubscribed'
  }

  get isStatusPaymentFailed() {
    return this.urlItem?.status === 'payment_failed'
  }
}
